import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import {
    ChevronLeft,
    ChevronRight,
    LayoutGrid,
    Square,
    X,
    Play,
    Pause,
    Crown,
} from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import useSeo from "../lib/useSeo";

export default function ReaderPage() {
    const { chapterId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();

    const [chapter, setChapter] = useState(null);
    const [mode, setMode] = useState("page"); // 'page' | 'panel'
    const [pageIndex, setPageIndex] = useState(0);
    const [autoplay, setAutoplay] = useState(false);

    useSeo({
        title: chapter
            ? `${chapter.title} — ${chapter.comic?.title || "Reader"}`
            : "Animated Comic Reader",
        description: chapter
            ? `Read ${chapter.title} from ${chapter.comic?.title} in cinematic page mode or panel-motion mode on EIGOO Books Hub.`
            : "Cinematic animated comic reader — EIGOO Books Hub.",
        image: chapter?.pages?.[0]?.image_url,
    });

    useEffect(() => {
        api.get(`/chapters/${chapterId}`).then(({ data }) => {
            setChapter(data);
            setPageIndex(0);
        });
    }, [chapterId]);

    // Track progress when authenticated
    const reportProgress = useCallback(
        async (idx, completed = false) => {
            if (!user) return;
            try {
                await api.post("/progress", {
                    chapter_id: chapterId,
                    page_index: idx,
                    completed,
                });
            } catch (e) {
                /* silent */
            }
        },
        [user, chapterId]
    );

    useEffect(() => {
        if (!chapter) return;
        const isLast = pageIndex >= chapter.pages.length - 1;
        reportProgress(pageIndex, isLast);
    }, [pageIndex, chapter, reportProgress]);

    // Autoplay (page mode)
    useEffect(() => {
        if (!autoplay || mode !== "page" || !chapter) return;
        const t = setTimeout(() => {
            if (pageIndex < chapter.pages.length - 1) setPageIndex((i) => i + 1);
            else setAutoplay(false);
        }, 4500);
        return () => clearTimeout(t);
    }, [autoplay, pageIndex, chapter, mode]);

    // Keyboard navigation
    useEffect(() => {
        const onKey = (e) => {
            if (mode !== "page" || !chapter) return;
            if (e.key === "ArrowRight" && pageIndex < chapter.pages.length - 1)
                setPageIndex((i) => i + 1);
            if (e.key === "ArrowLeft" && pageIndex > 0) setPageIndex((i) => i - 1);
            if (e.key === "Escape") navigate(-1);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [mode, pageIndex, chapter, navigate]);

    if (!chapter) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#0a0a0a]">
                <div className="eyebrow text-[#D4AF37] animate-pulse">Drawing the curtain…</div>
            </div>
        );
    }

    const pages = chapter.pages || [];
    const totalPages = pages.length;
    const progress = totalPages ? ((pageIndex + 1) / totalPages) * 100 : 0;

    return (
        <div
            data-testid="reader-page"
            className="fixed inset-0 bg-[#050505] text-white overflow-hidden"
        >
            {/* TOP BAR */}
            <header
                className="absolute top-0 left-0 right-0 z-40 backdrop-blur-xl bg-[#0a0a0a]/70 border-b"
                style={{ borderColor: "rgba(212,175,55,0.18)" }}
            >
                <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between gap-6">
                    <div className="flex items-center gap-4 min-w-0">
                        <button
                            data-testid="reader-close"
                            onClick={() => navigate(-1)}
                            className="text-[#A1A1AA] hover:text-[#D4AF37] transition-colors"
                            aria-label="Close reader"
                        >
                            <X className="w-5 h-5" />
                        </button>
                        <div className="min-w-0">
                            <div className="eyebrow text-[10px] flex items-center gap-2">
                                <Crown className="w-3 h-3" />
                                {chapter.comic?.title || "EIGOO Books"}
                            </div>
                            <div className="font-serif text-lg md:text-xl text-white truncate">
                                {chapter.title}
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center gap-2">
                        <button
                            data-testid="mode-page-btn"
                            onClick={() => setMode("page")}
                            className={`flex items-center gap-2 px-3 py-2 text-[10px] uppercase tracking-widest border transition-colors ${
                                mode === "page"
                                    ? "border-[#D4AF37] text-[#0a0a0a] bg-[#D4AF37]"
                                    : "border-[#D4AF37]/30 text-[#A1A1AA] hover:text-[#D4AF37]"
                            }`}
                        >
                            <Square className="w-3 h-3" /> Page mode
                        </button>
                        <button
                            data-testid="mode-panel-btn"
                            onClick={() => setMode("panel")}
                            className={`flex items-center gap-2 px-3 py-2 text-[10px] uppercase tracking-widest border transition-colors ${
                                mode === "panel"
                                    ? "border-[#D4AF37] text-[#0a0a0a] bg-[#D4AF37]"
                                    : "border-[#D4AF37]/30 text-[#A1A1AA] hover:text-[#D4AF37]"
                            }`}
                        >
                            <LayoutGrid className="w-3 h-3" /> Panel motion
                        </button>
                    </div>
                </div>
                {mode === "page" && (
                    <div
                        className="h-[2px] bg-[#D4AF37] transition-all duration-700 ease-out"
                        style={{ width: `${progress}%` }}
                        data-testid="reader-progress-bar"
                    />
                )}
            </header>

            {/* STAGE */}
            {mode === "page" ? (
                <PageMode
                    pages={pages}
                    pageIndex={pageIndex}
                    setPageIndex={setPageIndex}
                    autoplay={autoplay}
                    setAutoplay={setAutoplay}
                />
            ) : (
                <PanelMode pages={pages} chapter={chapter} />
            )}

            {/* CHAPTER COMPLETE BANNER */}
            {mode === "page" && pageIndex === totalPages - 1 && (
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.5, duration: 0.8 }}
                    className="absolute bottom-28 left-1/2 -translate-x-1/2 z-30 frame-gold px-8 py-4"
                    data-testid="chapter-complete"
                >
                    <div className="flex items-center gap-3">
                        <Crown className="w-4 h-4 text-[#D4AF37]" />
                        <span className="eyebrow">Chapter complete</span>
                        <Link
                            to={`/comic/${chapter.comic?.slug || "sovereign-commander"}`}
                            className="text-[#E6D59A] hover:text-white text-sm font-serif italic"
                        >
                            Return to volume →
                        </Link>
                    </div>
                </motion.div>
            )}
        </div>
    );
}

// -----------------------------------------------------------------------------
// PAGE MODE — cinematic crossfade with zoom + parallax
// -----------------------------------------------------------------------------
function PageMode({ pages, pageIndex, setPageIndex, autoplay, setAutoplay }) {
    const page = pages[pageIndex];
    if (!page) return null;

    return (
        <div
            data-testid="page-mode-stage"
            className="absolute inset-0 pt-20 pb-28 flex items-center justify-center page-stage"
        >
            <AnimatePresence mode="wait">
                <motion.div
                    key={pageIndex}
                    initial={{ opacity: 0, scale: 1.08, filter: "brightness(0.6)" }}
                    animate={{ opacity: 1, scale: 1, filter: "brightness(1)" }}
                    exit={{ opacity: 0, scale: 0.98, filter: "brightness(0.4)" }}
                    transition={{ duration: 1.0, ease: [0.22, 1, 0.36, 1] }}
                    className="relative max-w-4xl w-full mx-6 frame-gold p-2"
                    style={{ aspectRatio: "16/10" }}
                >
                    <div
                        className="absolute inset-2 bg-cover bg-center"
                        style={{ backgroundImage: `url(${page.image_url})` }}
                    />
                    <div
                        className="absolute inset-2 bg-gradient-to-t from-black/80 via-transparent to-black/20 pointer-events-none"
                    />
                    {/* SFX overlay */}
                    {page.sfx && (
                        <motion.div
                            key={`sfx-${pageIndex}`}
                            initial={{ opacity: 0, scale: 0.5, rotate: -8 }}
                            animate={{ opacity: 1, scale: 1, rotate: -4 }}
                            transition={{ delay: 0.4, duration: 0.6, type: "spring" }}
                            className="absolute top-10 right-10 font-serif italic text-5xl md:text-7xl tracking-tighter text-gold-gradient pointer-events-none select-none"
                            style={{
                                textShadow: "0 4px 30px rgba(212,175,55,0.45)",
                            }}
                            data-testid="sfx-text"
                        >
                            {page.sfx}
                        </motion.div>
                    )}
                    {/* Caption */}
                    {page.caption && (
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.6, duration: 0.7 }}
                            className="absolute bottom-6 left-6 right-6 md:left-10 md:right-10"
                        >
                            <div className="inline-block bg-[#0a0a0a]/85 border border-[#D4AF37]/30 px-5 py-3 max-w-xl">
                                <p
                                    className="font-serif italic text-lg md:text-2xl text-[#E6D59A] leading-snug"
                                    data-testid="page-caption"
                                >
                                    {page.caption}
                                </p>
                            </div>
                        </motion.div>
                    )}
                    {/* Page number */}
                    <div className="absolute top-4 left-6 eyebrow text-[10px] text-[#D4AF37]/80">
                        Page {pageIndex + 1} of {pages.length}
                    </div>
                </motion.div>
            </AnimatePresence>

            {/* Page nav arrows */}
            <button
                data-testid="prev-page-btn"
                onClick={() => setPageIndex((i) => Math.max(0, i - 1))}
                disabled={pageIndex === 0}
                className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center bg-[#0a0a0a]/70 border border-[#D4AF37]/30 hover:border-[#D4AF37] hover:bg-[#D4AF37]/10 disabled:opacity-20 disabled:cursor-not-allowed transition-all"
                aria-label="Previous page"
            >
                <ChevronLeft className="w-5 h-5 text-[#D4AF37]" />
            </button>
            <button
                data-testid="next-page-btn"
                onClick={() => setPageIndex((i) => Math.min(pages.length - 1, i + 1))}
                disabled={pageIndex === pages.length - 1}
                className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center bg-[#0a0a0a]/70 border border-[#D4AF37]/30 hover:border-[#D4AF37] hover:bg-[#D4AF37]/10 disabled:opacity-20 disabled:cursor-not-allowed transition-all"
                aria-label="Next page"
            >
                <ChevronRight className="w-5 h-5 text-[#D4AF37]" />
            </button>

            {/* Bottom toolbar */}
            <div
                data-testid="page-toolbar"
                className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2 backdrop-blur-md bg-black/70 border rounded-full px-4 py-2"
                style={{ borderColor: "rgba(212,175,55,0.25)" }}
            >
                <button
                    data-testid="autoplay-toggle"
                    onClick={() => setAutoplay((v) => !v)}
                    className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-[#D4AF37]/20 transition-colors"
                    aria-label="Toggle autoplay"
                >
                    {autoplay ? (
                        <Pause className="w-4 h-4 text-[#D4AF37]" />
                    ) : (
                        <Play className="w-4 h-4 text-[#D4AF37]" />
                    )}
                </button>
                <div className="flex items-center gap-1 px-2">
                    {pages.map((_, i) => (
                        <button
                            key={i}
                            onClick={() => setPageIndex(i)}
                            className={`h-1 rounded-full transition-all ${
                                i === pageIndex
                                    ? "w-8 bg-[#D4AF37]"
                                    : "w-2 bg-[#D4AF37]/30 hover:bg-[#D4AF37]/60"
                            }`}
                            aria-label={`Page ${i + 1}`}
                        />
                    ))}
                </div>
                <span className="px-2 text-xs text-[#A1A1AA] font-mono">
                    {String(pageIndex + 1).padStart(2, "0")} / {String(pages.length).padStart(2, "0")}
                </span>
            </div>
        </div>
    );
}

// -----------------------------------------------------------------------------
// PANEL MODE — scroll-triggered parallax motion comic
// -----------------------------------------------------------------------------
function PanelMode({ pages, chapter }) {
    const containerRef = useRef(null);

    return (
        <div
            ref={containerRef}
            data-testid="panel-mode-stage"
            className="absolute inset-0 pt-24 overflow-y-auto"
        >
            <div className="max-w-3xl mx-auto px-6 pb-32">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9 }}
                    className="text-center pt-8 pb-20"
                >
                    <div className="eyebrow text-[10px] mb-3 flex items-center justify-center gap-2">
                        <Crown className="w-3 h-3" /> {chapter.comic?.title}
                    </div>
                    <h2 className="font-serif text-4xl md:text-6xl text-white">
                        {chapter.title}
                    </h2>
                    <div className="gold-rule mt-8 max-w-xs mx-auto" />
                    <p className="mt-6 text-[#A1A1AA] text-sm uppercase tracking-[0.32em]">
                        Scroll to begin
                    </p>
                </motion.div>

                <div className="space-y-32">
                    {pages.map((p, i) => (
                        <PanelItem key={i} page={p} index={i} />
                    ))}
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8 }}
                    className="text-center mt-32 pb-20"
                >
                    <Crown className="w-6 h-6 text-[#D4AF37] mx-auto mb-4" />
                    <div className="font-serif italic text-3xl text-gold-gradient">
                        End of chapter
                    </div>
                    <Link
                        to={`/comic/${chapter.comic?.slug}`}
                        className="btn-ghost-gold mt-8 inline-flex"
                    >
                        Return to volume
                    </Link>
                </motion.div>
            </div>
        </div>
    );
}

function PanelItem({ page, index }) {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start end", "end start"],
    });
    const y = useTransform(scrollYProgress, [0, 1], [60, -60]);
    const scale = useTransform(scrollYProgress, [0, 0.5, 1], [0.95, 1.02, 1]);

    return (
        <motion.div
            ref={ref}
            data-testid={`panel-item-${index}`}
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: "-15%" }}
            transition={{ duration: 0.9 }}
        >
            <div className="eyebrow mb-4 text-[10px]">Panel {String(index + 1).padStart(2, "0")}</div>
            <motion.div
                style={{ y, scale }}
                className="frame-gold p-1 relative overflow-hidden"
            >
                <div
                    className="w-full bg-cover bg-center"
                    style={{
                        aspectRatio: index % 2 === 0 ? "16/10" : "4/5",
                        backgroundImage: `url(${page.image_url})`,
                    }}
                />
                {page.sfx && (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.6, rotate: -6 }}
                        whileInView={{ opacity: 1, scale: 1, rotate: -3 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.3, type: "spring", stiffness: 80 }}
                        className="absolute top-6 right-6 font-serif italic text-4xl md:text-6xl tracking-tighter text-gold-gradient select-none pointer-events-none"
                        style={{ textShadow: "0 4px 30px rgba(212,175,55,0.5)" }}
                    >
                        {page.sfx}
                    </motion.div>
                )}
            </motion.div>
            {page.caption && (
                <motion.p
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.7, delay: 0.2 }}
                    className="font-serif italic text-xl md:text-2xl text-[#E6D59A] mt-6 leading-relaxed max-w-xl"
                >
                    {page.caption}
                </motion.p>
            )}
        </motion.div>
    );
}
